import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const DataTable = ({ data, onDataChange, auditorFilesData, docType, loadingFiles, conversionError,isMobile }) => {
  const [tableData, setTableData] = useState([]);
  const [editCell, setEditCell] = useState({ rowIndex: null, colName: null });
  const [columns, setColumns] = useState([]);
  const [auditorBase64List, setAuditorBase64List] = useState([]);
  const [previewPdfBase64, setPreviewPdfBase64] = useState(null); // PDF preview base64
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);

  const base64ToUint8Array = (base64) => {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  const loadPdf = async (base64Pdf) => {
    try {
      const pdfData = base64ToUint8Array(base64Pdf);
      const doc = await pdfjsLib.getDocument({ data: pdfData }).promise;
      setPdfDoc(doc);
      setNumPages(doc.numPages);
      setCurrentPage(1);
      renderPage(doc, 1);
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  };

  const renderPage = async (doc, pageNum) => {
    const page = await doc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
  };

  const nextPage = () => {
    if (pdfDoc && currentPage < numPages) {
      const next = currentPage + 1;
      setCurrentPage(next);
      renderPage(pdfDoc, next);
    }
  };

  const prevPage = () => {
    if (pdfDoc && currentPage > 1) {
      const prev = currentPage - 1;
      setCurrentPage(prev);
      renderPage(pdfDoc, prev);
    }
  };

  useEffect(() => {
    setTableData(JSON.parse(JSON.stringify(data || [])));
    if (data && data.length > 0) {
      // Get union of all keys from all rows
      const allKeysSet = new Set();
      data.forEach(row => {
        Object.keys(row).forEach(key => {
          allKeysSet.add(key);
        });
      });
      const allKeys = Array.from(allKeysSet);
      setColumns(allKeys.filter(col => !['UniqueID', 'DocID', 'ID'].includes(col)));
    }
  }, [data]);

  const handleCellClick = (rowIndex, colName) => setEditCell({ rowIndex, colName });
  const handleCellChange = (e, rowIndex, colName) => {
    const newData = [...tableData];
    newData[rowIndex][colName] = e.target.value;
    setTableData(newData);
    onDataChange && onDataChange(newData);
  };
  const handleBlur = () => setEditCell({ rowIndex: null, colName: null });
  const handleKeyDown = (e) => e.key === 'Enter' && handleBlur();

  const removeRow = (index) => {
    Swal.fire({
      html: `<div class="swal-custom-header"><span class="swal-custom-title">Remove Row</span></div>
             <hr class="swal-divider"/>
             <p class="swal-warning-text"><strong>Warning: </strong>This process is irreversible. Are you sure you want to remove the row?</p>`,
      showCloseButton: true,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Remove',
      cancelButtonText: 'Close',
      customClass: { popup: 'swal-popup', confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
      allowOutsideClick: false,
      allowEscapeKey: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const newData = [...tableData];
        newData.splice(index, 1);
        setTableData(newData);
        onDataChange && onDataChange(newData);
      }
    });
  };

  const renderCell = (row, rowIndex, colName, displayValue) => (
    editCell.rowIndex === rowIndex && editCell.colName === colName
      ? <input
          type="text"
          value={row[colName] || ''}
          onChange={(e) => handleCellChange(e, rowIndex, colName)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      : <div className="editable-cell" onClick={() => handleCellClick(rowIndex, colName)}>{displayValue || ''}</div>
  );

  if (loadingFiles && (docType === 'auditor_assistant' || docType === 'timesheet_qb_enterprise')) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', fontSize: '18px', color: '#09A3D9' }}>
        <div className="spinner-border" role="status" style={{ marginBottom: '10px' }}></div>
        <div>Converting files to PDF, please wait...</div>
      </div>
    );
  }

  if (conversionError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', fontSize: '18px', color: '#dc3545' }}>
        There was an error getting your files. Please try again later.
      </div>
    );
  }

  if (!data || data.length === 0) {
    const defaultColumns = ['fileName', 'calendarYear', 'formType', 'payorName', 'payorAddress', 'payorCity', 'payorState', 'payorCountry'];
    const displayColumns = columns.length > 0 ? columns : defaultColumns;
    return (
      <div className="file-table-container">
        <table className="file-table">
          <thead>
            <tr>
              <th className='dataPreviewTableHeader'>Action</th>
              {displayColumns.map(column => <th className='dataPreviewTableHeader' key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={displayColumns.length + 1}>No data available</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      {docType && (docType === 'auditor_assistant' || docType === 'timesheet_qb_enterprise') && auditorFilesData && auditorFilesData.length > 0 ? (
        auditorFilesData.map((file, index) => (
          <div key={index}>
            <div className="selected-files">
              <li className="file-item" style={{ padding: '8px 100px' }}>
                <span className="file-type">{file.type.split('/')[1].toUpperCase()}</span>
                <span 
                  className="file-name" 
                  style={{ cursor: 'pointer' }}
                  title={file.name}
                  onClick={() => {
                    setPreviewPdfBase64(file.base64);
                    loadPdf(file.base64);
                  }}
                >
                  {file.name}
                </span>
                <span className="file-size">({file.size || 'Unknown'})</span>
              </li>
            </div>
          </div>
        ))
      ) : (
        <div className="file-table-container">
          <table className="file-table">
            <thead>
              <tr>
                <th className='dataPreviewTableHeader'>Action</th>
                {columns.map(column => (
                  <th style={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "250px", minWidth: "200px" }} key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index}>
                  <td>
                    <button className="remove-row-btn" onClick={() => removeRow(index)} title="Remove row">✕</button>
                  </td>
                  {columns.map(column => (
                    <td style={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "250px", minWidth: "200px" }} key={`${index}-${column}`}>
                      {renderCell(row, index, column, row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewPdfBase64 && (
        <div 
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setPreviewPdfBase64(null)}
        >
          <div 
            style={{ width: '90%', height: isMobile ? '75%' : '90%', backgroundColor: 'white', padding: '10px', position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{ position: 'absolute', top: 10, right: 10, zIndex: 1001, background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer' }}
              onClick={() => setPreviewPdfBase64(null)}
            >
              Close
            </button>
            <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
              <button onClick={prevPage} disabled={currentPage === 1}>Prev</button>
              <span>Page {currentPage} of {numPages}</span>
              <button onClick={nextPage} disabled={currentPage === numPages}>Next</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DataTable;
